import { createClient } from '@supabase/supabase-js'

// Supabase configuration - hardcoded for Hostinger
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://kvvipthrvmxkewetebln.supabase.co'
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt2dmlwdGhydm14a2V3ZXRlYmxuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQ4Nzg2NzAsImV4cCI6MjA3MDQ1NDY3MH0.ZcvZqS2UnDLhACDSEda4IrVvavkUsXcPOjDIejv-jV0'

if (!supabaseUrl || !supabaseKey) {
  throw new Error(
    'Missing Supabase configuration.'
  )
}
// Vehicle table name (configurable). Defaults to existing table used elsewhere.
const TABLE_NAME = import.meta.env.VITE_SUPABASE_TABLE || 'vehicles_db2025'

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true
  }
})

// Always use real Supabase - no demo mode
console.log('🚀 Connected to Supabase database')

// Vehicle database functions
export const vehicleAPI = {
  // Get all vehicles
  async getVehicles() {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Add new vehicle
  async addVehicle(vehicle) {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert([{
        make: vehicle.make,
        model: vehicle.model,
        year: parseInt(vehicle.year) || new Date().getFullYear(),
        price: parseFloat(vehicle.price) || 0,
        mileage: parseInt(vehicle.mileage) || 0,
        vin: vehicle.vin,
        stock_number: vehicle.stockNumber,
        fuel_type: vehicle.fuelType,
        transmission: vehicle.transmission,
        condition: vehicle.condition,
        exterior_color: vehicle.exteriorColor,
        interior_color: vehicle.interiorColor,
        drivetrain: vehicle.drivetrain,
        engine: vehicle.engine,
        doors: parseInt(vehicle.doors) || 4,
        seating: parseInt(vehicle.seating) || 5,
        city_mpg: parseInt(vehicle.cityMpg) || 0,
        highway_mpg: parseInt(vehicle.highwayMpg) || 0,
        combined_mpg: parseInt(vehicle.combinedMpg) || 0,
        description: vehicle.description,
        features: vehicle.features || [],
        images: vehicle.images || [],
        videos: vehicle.videos || [],
        vehicle_type: vehicle.vehicleType || 'vehicle',
        status: vehicle.status || 'on-lot',
        // Trailer-specific fields
        length: vehicle.length ? parseFloat(vehicle.length) : null,
        width: vehicle.width ? parseFloat(vehicle.width) : null,
        capacity: vehicle.capacity ? parseInt(vehicle.capacity) : null,
        axles: vehicle.axles || null,
        trailer_type: vehicle.trailerType || null
      }])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  // Update vehicle
  async updateVehicle(id, vehicle) {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update({
        id: vehicle.id,
        created_at: vehicle.createdAt,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
        price: vehicle.price,
        mileage: vehicle.mileage,
        vin: vehicle.vin,
        stock_number: vehicle.stockNumber,
        fuel_type: vehicle.fuelType,
        transmission: vehicle.transmission,
        condition: vehicle.condition,
        exterior_color: vehicle.exteriorColor,
        interior_color: vehicle.interiorColor,
        drivetrain: vehicle.drivetrain,
        engine: vehicle.engine,
        city_mpg: vehicle.cityMpg || 0,
        highway_mpg: vehicle.highwayMpg || 0,
        combined_mpg: vehicle.combinedMpg || 0,
        description: vehicle.description,
        features: vehicle.features || [],
        images: vehicle.images || [],
        videos: vehicle.videos || [],
        vehicle_type: vehicle.vehicleType || 'vehicle',
        status: vehicle.status || 'on-lot',
        // Trailer-specific fields
        length: vehicle.length ? parseFloat(vehicle.length) : null,
        width: vehicle.width ? parseFloat(vehicle.width) : null,
        capacity: vehicle.capacity ? parseInt(vehicle.capacity) : null,
        axles: vehicle.axles || null,
        trailer_type: vehicle.trailerType || null
      })
      .eq('id', id)
      .select();
    
    if (error) throw error;
    return data[0];
  },

  // Delete vehicle
  async deleteVehicle(id) {
    // First check if there are any deals for this vehicle
    const { data: deals, error: checkError } = await supabase
      .from('deals')
      .select('id')
      .eq('vehicle_id', id);
    
    if (checkError) {
      console.error('Error checking deals:', checkError);
    }
    
    // Delete deals if they exist
    if (deals && deals.length > 0) {
      const { error: dealsError } = await supabase
        .from('deals')
        .delete()
        .eq('vehicle_id', id);
      
      if (dealsError) {
        console.error('Error deleting deals:', dealsError);
        throw new Error(`Cannot delete vehicle: ${deals.length} deal(s) exist. Please delete the deals first or contact support.`);
      }
    }
    
    // Delete from leads table using vehicle_of_interest column
    const { error: leadsError } = await supabase
      .from('leads')
      .delete()
      .eq('vehicle_of_interest', id);
    
    if (leadsError) {
      console.error('Error deleting leads:', leadsError);
      // Don't throw here as leads might not exist
    }

    // Now delete the vehicle
    const { error } = await supabase
      .from(TABLE_NAME)
      .delete()
      .eq('id', id);
    
    if (error) {
      if (error.code === '23503') {
        throw new Error('Cannot delete vehicle: It has associated records (deals, leads, etc.). Please delete those first.');
      }
      throw error;
    }
  }
}

// Leads database functions
export const leadsAPI = {
  // Get all leads
  async getLeads(filters = {}) {
    let query = supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.lead_type) {
      query = query.eq('lead_type', filters.lead_type);
    }
    if (filters.priority) {
      query = query.eq('priority', filters.priority);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Delete lead
  async deleteLead(leadId) {
    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', leadId);
    
    if (error) throw error;
    return true;
  },

  // Add new lead
  async addLead(lead) {
    const { data, error } = await supabase
      .from('leads')
      .insert([{
        first_name: lead.firstName,
        last_name: lead.lastName,
        email: lead.email,
        phone: lead.phone,
        lead_type: lead.leadType,
        lead_source: lead.leadSource || 'website',
        status: lead.status || 'new',
        priority: lead.priority || 'medium',
        interested_vehicle_id: lead.interestedVehicleId,
        vehicle_make: lead.vehicleMake,
        vehicle_model: lead.vehicleModel,
        vehicle_year: lead.vehicleYear,
        budget_min: lead.budgetMin,
        budget_max: lead.budgetMax,
        message: lead.message,
        preferred_contact_method: lead.preferredContactMethod || 'email',
        preferred_contact_time: lead.preferredContactTime,
        trade_in_make: lead.tradeInMake,
        trade_in_model: lead.tradeInModel,
        trade_in_year: lead.tradeInYear,
        trade_in_mileage: lead.tradeInMileage,
        trade_in_condition: lead.tradeInCondition,
        financing_needed: lead.financingNeeded || false,
        credit_score_range: lead.creditScoreRange,
        down_payment: lead.downPayment,
        assigned_to: lead.assignedTo,
        next_follow_up: lead.nextFollowUp
      }])
      .select();
    if (error) throw error;
    return data[0];
  },

  // Update lead
  async updateLead(id, updates) {
    const { data, error } = await supabase
      .from('leads')
      .update({
        first_name: updates.firstName,
        last_name: updates.lastName,
        email: updates.email,
        phone: updates.phone,
        status: updates.status,
        priority: updates.priority,
        assigned_to: updates.assignedTo,
        last_contact_date: updates.lastContactDate,
        next_follow_up: updates.nextFollowUp,
        conversion_date: updates.conversionDate,
        notes: updates.notes
      })
      .eq('id', id)
      .select();

    if (error) throw error;
    return data[0];
  },

  // Add note to lead
  async addNote(leadId, note) {
    // First get current notes
    const { data: lead, error: fetchError } = await supabase
      .from('leads')
      .select('notes')
      .eq('id', leadId)
      .single();

    if (fetchError) throw fetchError;

    const currentNotes = lead.notes || [];
    const newNote = {
      id: Date.now(),
      text: note,
      created_at: new Date().toISOString(),
      created_by: 'admin' // You can make this dynamic
    };

    const updatedNotes = [...currentNotes, newNote];

    const { data, error } = await supabase
      .from('leads')
      .update({ notes: updatedNotes })
      .eq('id', leadId)
      .select();

    if (error) throw error;
    return data[0];
  }
};

// Contact inquiries database functions
export const contactAPI = {
  // Get all contact inquiries
  async getInquiries(filters = {}) {
    let query = supabase
      .from('contact_inquiries')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.inquiry_type) {
      query = query.eq('inquiry_type', filters.inquiry_type);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Add new contact inquiry
  async addInquiry(inquiry) {
    console.log('API received inquiry data:', inquiry);
    const insertData = {
      name: inquiry.name,
      email: inquiry.email,
      phone: inquiry.phone || null,
      subject: inquiry.subject || 'General Inquiry',
      message: inquiry.message,
      inquiry_type: inquiry.inquiryType || 'general'
    };
    console.log('Inserting data:', insertData);
    
    const { data, error } = await supabase
      .from('contact_inquiries')
      .insert([insertData])
      .select();

    if (error) {
      console.error('Supabase insert error:', error);
      throw error;
    }
    console.log('Supabase insert success:', data);
    return data[0];
  },

  // Update inquiry status
  async updateInquiry(id, updates) {
    const { data, error } = await supabase
      .from('contact_inquiries')
      .update({
        status: updates.status,
        priority: updates.priority,
        responded_at: updates.respondedAt,
        responded_by: updates.respondedBy,
        response_message: updates.responseMessage
      })
      .eq('id', id)
      .select();

    if (error) throw error;
    return data[0];
  },

  // Delete inquiry
  async deleteInquiry(id) {
    const { error } = await supabase
      .from('contact_inquiries')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

export default supabase